import axios from 'axios';
import { REFRESH_LEAD_MS } from './config';
import type { DeviceIdStore } from './deviceId';
import type { AuthEnvironment } from './environment';
import { decodeExpMs } from './jwt';
import { runExclusive } from './locks';
import type { AuthNamespace } from './namespace';
import type { RefreshClient } from './refreshClient';
import type { TokenStore } from './tokenStore';

/** `reason` отказа ротации: потолок жизни SAML-семейства — повторный вход через IdP. */
export const REAUTH_REQUIRED_REASON = 'reauth_required';
/** `reason` отказа ротации: сбой сервера (старый бэкенд отвечает им и на 401) — не выход. */
const SERVER_ERROR_REASON = 'server_error';

/** `reason` из тела отказа RefreshToken или undefined. */
export const refreshFailureReason = (error: unknown): string | undefined => {
    if (!axios.isAxiosError(error)) return undefined;
    const reason = (error.response?.data as { reason?: unknown } | undefined)?.reason;
    return typeof reason === 'string' ? reason : undefined;
};

/** 401/403 ротации, кроме `server_error`: refresh больше не годится. */
const isFinalFailure = (error: unknown): boolean => {
    if (!axios.isAxiosError(error)) return false;
    const status = error.response?.status;
    return (status === 401 || status === 403) && refreshFailureReason(error) !== SERVER_ERROR_REASON;
};

/** Стенд недоступен: сеть, 502/503/504, `server_error`. */
const isApiUnavailable = (error: unknown): boolean =>
    axios.isAxiosError(error) &&
    (refreshFailureReason(error) === SERVER_ERROR_REASON ||
        !error.response ||
        [502, 503, 504].includes(error.response.status));

export interface RefreshOrchestratorOptions {
    namespace: Pick<AuthNamespace, 'refreshLockName'>;
    tokens: TokenStore;
    client: RefreshClient;
    deviceId: Pick<DeviceIdStore, 'get'>;
    env: Pick<AuthEnvironment, 'locks' | 'now'>;
    /** Окончательный отказ ротации (401/403, кроме `server_error`): выход или повторный вход через IdP. */
    onRejected: (error: unknown) => void;
    onApiUnavailable: () => void;
    /** Временный отказ ротации (сеть, 5xx, `server_error`): refresh цел, вход не отозван — вызывающий повторит позже. */
    onTransientFailure: () => void;
    /**
     * Пока шёл POST, сессию сняли (выход здесь или в соседней вкладке): пришедший access не записывается — им
     * отзывается семейство (SignOut), иначе новый refresh пережил бы выход. Отказ отзыва глотается.
     */
    revoke: (accessToken: string) => Promise<unknown>;
}

export interface RefreshOrchestrator {
    /**
     * Ротация access: в вкладке — один общий промис, между вкладками — Web Lock. true — access актуален (обновлён
     * здесь или соседней вкладкой). `rejectedAccessToken` — токен запроса, получившего 401: ротация принудительная,
     * пока его не сменила другая вкладка.
     */
    refresh(rejectedAccessToken?: string | null): Promise<boolean>;
}

/**
 * Single-flight ротация refresh-токена. Параллельные POST одним refresh сервер считает reuse и выкидывает все вкладки,
 * поэтому между вкладками — Web Lock namespace, а внутри вкладки — общий промис.
 */
export function createRefreshOrchestrator({
    namespace,
    tokens,
    client,
    deviceId,
    env,
    onRejected,
    onApiUnavailable,
    onTransientFailure,
    revoke,
}: RefreshOrchestratorOptions): RefreshOrchestrator {
    let inFlight: Promise<boolean> | null = null;

    const isAccessFresh = (): boolean => {
        const access = tokens.getAccessToken();
        const expMs = access ? decodeExpMs(access) : null;
        return expMs !== null && expMs - env.now() > REFRESH_LEAD_MS;
    };

    /**
     * Токен уже обновлён — ротировать не нужно. После 401 решает не срок токена, а то, сменила ли его другая вкладка:
     * сервер мог завершить сессию при ещё живом JWT (отзыв входа в админке), и без принудительной ротации запросы
     * упирались бы в 401 до конца срока токена.
     */
    const isAlreadyRefreshed = (rejectedAccessToken?: string | null): boolean =>
        rejectedAccessToken ? tokens.getAccessToken() !== rejectedAccessToken : isAccessFresh();

    const fail = (error: unknown) => {
        if (isFinalFailure(error)) {
            onRejected(error);
            return;
        }
        onTransientFailure();
        if (isApiUnavailable(error)) onApiUnavailable();
    };

    const withLock = (task: () => Promise<boolean>) => runExclusive(env.locks, namespace.refreshLockName, task);

    /** Результат ротации, начатой до выхода: не писать, семейство отозвать. */
    const discard = async (accessToken: string) => {
        try {
            await revoke(accessToken);
        } catch {
            // семейство отзовёт и SignOut вкладки, которая выходит
        }
        return false;
    };

    const refreshBody = (rejectedAccessToken?: string | null): Promise<boolean> => {
        const refreshAtEnter = tokens.getRefreshToken();
        // Без refresh не ротируем и не чистим хранилище: иначе 401 запроса профиля при параллельном входе стёр бы только
        // что записанный access. Выход в этой ветке решает recoverFromUnauthorized.
        if (!refreshAtEnter) return Promise.resolve(false);
        return withLock(async () => {
            const refreshToken = tokens.getRefreshToken();
            if (!refreshToken) return false;
            // Другая вкладка положила новый refresh, пока ждали замок.
            if (refreshToken !== refreshAtEnter || isAlreadyRefreshed(rejectedAccessToken)) return true;
            const epoch = tokens.epoch();
            try {
                const next = await client.postRefreshToken(refreshToken, deviceId.get(), 'body');
                if (tokens.epoch() !== epoch) return await discard(next.accessToken);
                tokens.setTokens({ accessToken: next.accessToken, refreshToken: next.refreshToken }, 'refresh');
                return true;
            } catch (error) {
                if (tokens.epoch() !== epoch) return false;
                // Пока шёл POST, другая вкладка положила новый refresh — наш отказ это её успех, не выход.
                const current = tokens.getRefreshToken();
                if (current && current !== refreshAtEnter) return false;
                fail(error);
                return false;
            }
        });
    };

    const refreshCookie = (rejectedAccessToken?: string | null): Promise<boolean> => {
        const epoch = tokens.epoch();
        return withLock(async () => {
            // Сессию сняли, пока ждали замок (выход, парковка учётной записи): cookie уже не её — запроса нет.
            if (tokens.epoch() !== epoch) return false;
            // Соседняя вкладка могла обновить access в памяти этой (канал), пока ждали замок.
            if (isAlreadyRefreshed(rejectedAccessToken)) return true;
            const accessAtEnter = tokens.getAccessToken();
            try {
                const next = await client.postRefreshToken(null, deviceId.get(), 'cookie');
                if (tokens.epoch() !== epoch) return await discard(next.accessToken);
                // refresh — в httpOnly cookie, в хранилище только access
                tokens.setTokens({ accessToken: next.accessToken, refreshToken: null }, 'refresh');
                return true;
            } catch (error) {
                if (tokens.epoch() !== epoch) return false;
                // Пока шёл POST, соседняя вкладка ротировала общую cookie и прислала свежий access — наш отказ это её
                // успех. Отклонённый же access, сколько бы ему ни осталось жить, отказ не отменяет: сервер завершил
                // сессию при живом JWT (отзыв в «Сеансах»).
                if (tokens.getAccessToken() !== accessAtEnter && isAccessFresh()) return false;
                fail(error);
                return false;
            }
        });
    };

    return {
        refresh(rejectedAccessToken) {
            if (inFlight) return inFlight;
            const mode = tokens.getMode();
            inFlight = (async () => {
                try {
                    return mode === 'cookie'
                        ? await refreshCookie(rejectedAccessToken)
                        : await refreshBody(rejectedAccessToken);
                } finally {
                    inFlight = null;
                }
            })();
            return inFlight;
        },
    };
}
